import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  DUE_RATIO,
  currencySymbol,
  formatMoney,
  purchaseRhythm,
  routeStores,
  toCzkMinor,
  type CatalogItem,
  type Currency,
  type Estimate,
  type Offer,
  type PurchaseRhythm,
  type SyncChangeData,
  type TripItem,
  type TripItemPatch,
  offerPatch,
} from '@sp/shared'
import { api } from '../../lib/api.ts'
import { HttpError } from '../../lib/http.ts'
import { useCatalog, useHistory, useOfferMatches, useTemplates } from '../../lib/groupData.ts'
import { matchesByItem, offersForStore } from '../../lib/offers.ts'
import { useDesktop } from '../../lib/useDesktop.ts'
import {
  estimateText,
  everyText,
  formatQty,
  itemsCount,
  joinNames,
  parsePriceInput,
  plural,
  priceInputValue,
  relativeAgo,
  searchKey,
  storeShortName,
  tripName,
} from '../../lib/format.ts'
import { newId } from '../../lib/storage.ts'
import { errorMessage } from '../../lib/useResource.ts'
import { Icon } from '../../components/Icon.tsx'
import { Sheet } from '../../components/Sheet.tsx'
import {
  Button,
  Checkbox,
  Chip,
  EmptyState,
  Footer,
  IconButton,
  Label,
  Paragraph,
  ScreenHeader,
  SectionHead,
  SelectRow,
  Spinner,
  Stepper,
  TextCol,
  TextField,
} from '../../components/ui.tsx'
import { DeskToolbar, Inspector, MoneyLine, Panel } from '../../components/desk.tsx'
import { OfferBadge } from '../../components/OfferBadge.tsx'
import { OfferSheet } from '../../components/OfferSheet.tsx'
import { ratesFor, routeOf, type TripViewProps } from './shared.ts'
import { ScrollRow } from '../../components/ScrollRow.tsx'

/** „Dlouho nekoupeno" = naposledy pred vic nez peti tydny (mesicni nakupy + rezerva). */
const STALE_MS = 35 * 24 * 60 * 60 * 1000

/** Cena nastavena ve vyberu: null = bez ceny; toTemplate = zapsat i jako obvyklou cenu; offer = cena z akce. */
interface PriceDraft {
  minor: number | null
  toTemplate: boolean
  offer?: Offer | undefined
}

/**
 * Vyber ze sablony (Crouton / Amazon Fresh v navrhu). Ukazuje i to, co uz
 * na nakupu je, takze jde mnozstvi upravit nebo polozku odebrat.
 *
 * Koncept drzi jen vlastni zmeny (polozka sablony -> mnozstvi, null = nechci;
 * zvlast cena), ne snimek celeho vyberu. Co mezitim pridal nebo zmenil nekdo
 * jiny, se pri potvrzeni neodebere ani nevrati.
 *
 * Na pocitaci jsou obchody sloupce a vpravo panel Vybráno jako kosik.
 */
export function PickerView({ trip, sync, stores, fx }: TripViewProps) {
  const navigate = useNavigate()
  const desktop = useDesktop()
  const catalog = useCatalog(trip.groupId)
  const templates = useTemplates(trip.groupId)
  const [searchParams] = useSearchParams()
  /** Vybrana sablona; z odkazu „Přidat do nákupu" na strance sablony, jinak Pravidelny nakup. */
  const [templateId, setTemplateId] = useState<string | null>(() => searchParams.get('sablona'))
  const [routeBusy, setRouteBusy] = useState(false)
  const [filter, setFilter] = useState<string>('all')
  const [searching, setSearching] = useState(false)
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<Map<string, number | null>>(() => new Map())
  const [prices, setPrices] = useState<Map<string, PriceDraft>>(() => new Map())
  const [pricing, setPricing] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [offerOpen, setOfferOpen] = useState<{ offer: Offer; ci: CatalogItem } | null>(null)
  // Akce platne v den nakupu; nakup bez data bere akce platne ted.
  const offerMatches = useOfferMatches(trip.groupId, trip.plannedDate)
  const history = useHistory(trip.groupId)

  const existing = useMemo(() => {
    const map = new Map<string, TripItem>()
    for (const item of trip.items) if (item.catalogItemId) map.set(item.catalogItemId, item)
    return map
  }, [trip.items])

  const allItems = catalog.res.state === 'ok' ? catalog.res.data : []
  const byId = useMemo(() => new Map(allItems.map((ci) => [ci.id, ci])), [allItems])
  const templateList = templates.res.state === 'ok' ? templates.res.data : []
  const template = templateList.find((t) => t.id === templateId) ?? templateList.find((t) => t.regular) ?? null
  const members = useMemo(() => new Map((template?.items ?? []).map((i) => [i.catalogItemId, i.qty])), [template])
  // Dokud se sablony nenactou, ukaze se vse zbozi (Pravidelny nakup je skoro vzdy vsechno).
  const items = template ? allItems.filter((ci) => members.has(ci.id)) : allItems
  const route = routeOf(trip, stores)
  const routeIds = new Set(route.map((store) => store.id))
  const groupStores = routeStores(stores)
  /** Mnozstvi, ktere se nabidne: z vybrane sablony, jinak obvykle. */
  const defaultQtyOf = (ci: CatalogItem) => members.get(ci.id) ?? ci.defaultQty
  /** Po pridani jine sablony nez Pravidelneho nakupu nabidne planovani nakup pojmenovat podle ni. */
  function back(suggestTitle?: string) {
    navigate(`/nakup/${trip.id}`, suggestTitle ? { state: { suggestTitle } } : undefined)
  }

  // Jina sablona nez Pravidelny nakup se vybere cela (grilovacka); co uz na nakupu je, se preskoci.
  const preselected = useRef<string | null>(null)
  useEffect(() => {
    if (!template || template.regular || allItems.length === 0 || preselected.current === template.id) return
    preselected.current = template.id
    setDraft((prev) => {
      const next = new Map(prev)
      for (const entry of template.items) {
        const ci = byId.get(entry.catalogItemId)
        if (ci && !existing.has(ci.id) && !next.has(ci.id)) next.set(ci.id, entry.qty ?? ci.defaultQty)
      }
      return next
    })
  }, [template, allItems.length, byId, existing])

  // Rytmus nakupu polozek sablony z dokoncenych nakupu.
  const rhythms = useMemo(() => {
    const map = new Map<string, { rhythm: PurchaseRhythm; last: string }>()
    for (const entry of history.res.state === 'ok' ? history.res.data : []) {
      const rhythm = purchaseRhythm(entry.purchases.map((p) => p.date))
      const last = entry.purchases[0]?.date
      if (rhythm && last) map.set(entry.catalogItemId, { rhythm, last })
    }
    return map
  }, [history.res])
  const isDue = (ci: CatalogItem) => (rhythms.get(ci.id)?.rhythm.dueRatio ?? 0) >= DUE_RATIO

  function qtyOf(id: string): number | null {
    return draft.has(id) ? (draft.get(id) ?? null) : (existing.get(id)?.qty ?? null)
  }

  function storeIdFor(ci: CatalogItem): string | null {
    const onTrip = existing.get(ci.id)
    if (onTrip) return onTrip.storeId ?? ci.storeId ?? route[0]?.id ?? null
    // Zbozi z obchodu mimo trasu jde do prvniho obchodu na trase; nad seznamem se nabidne obchod pridat.
    if (ci.storeId && (routeIds.has(ci.storeId) || route.length === 0)) return ci.storeId
    return route[0]?.id ?? ci.storeId ?? null
  }

  function currencyFor(ci: CatalogItem): Currency | null {
    const store = stores.find((s) => s.id === storeIdFor(ci))
    return existing.get(ci.id)?.expectedCurrency ?? ci.typicalCurrency ?? store?.currency ?? null
  }

  /** Cena, se kterou polozka na nakup pujde (nebo uz na nem je). */
  function priceOf(ci: CatalogItem): number | null {
    const set = prices.get(ci.id)
    if (set) return set.minor
    const item = existing.get(ci.id)
    return item ? item.expectedPriceMinor : ci.typicalPriceMinor
  }

  function select(ci: CatalogItem, qty: number | null) {
    const value = qty !== null && qty > 0 ? qty : null
    const next = new Map(draft)
    if (value === (existing.get(ci.id)?.qty ?? null)) next.delete(ci.id)
    else next.set(ci.id, value)
    setDraft(next)
  }

  // Zmeny se pocitaji az proti aktualnimu stavu nakupu, ne proti tomu pri otevreni.
  const plan = [...new Set([...draft.keys(), ...prices.keys()])].flatMap((id) => {
    const ci = byId.get(id)
    if (!ci) return []
    return [{ ci, item: existing.get(id), qty: qtyOf(id), price: priceOf(ci) }]
  })
  const adds = plan.filter((p) => p.qty !== null && !p.item)
  const removals = plan.filter((p) => p.qty === null && p.item)
  const updates = plan.filter(
    (p) => p.qty !== null && p.item && (p.item.qty !== p.qty || p.item.expectedPriceMinor !== p.price),
  )
  const templatePrices = plan.filter(
    (p) => p.qty !== null && prices.get(p.ci.id)?.toTemplate && p.price !== p.ci.typicalPriceMinor,
  )
  const changed = adds.length + removals.length + updates.length + templatePrices.length > 0
  const onlyAdds = adds.length > 0 && removals.length === 0 && updates.length === 0

  // Velky nakup: zbozi z obchodu, ktery na trase neni, jde do prvniho obchodu — nebo se obchod prida.
  const offRouteStores =
    route.length > 0
      ? [...new Set(adds.flatMap((p) => (p.ci.storeId && !routeIds.has(p.ci.storeId) ? [p.ci.storeId] : [])))].flatMap(
          (id) => stores.find((store) => store.id === id && !store.archived) ?? [],
        )
      : []
  const offRouteCount = adds.filter((p) => offRouteStores.some((store) => store.id === p.ci.storeId)).length

  async function addToRoute() {
    setRouteBusy(true)
    setError(null)
    try {
      await sync.updateTrip({ storeIds: [...trip.storeIds, ...offRouteStores.map((store) => store.id)] })
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Trasu jde změnit jen se signálem.' : errorMessage(err))
    } finally {
      setRouteBusy(false)
    }
  }

  const firstStore = route[0]
  const offRouteNotice = offRouteStores.length > 0 && firstStore && (
    <div className="pick-offroute">
      <Paragraph size="meta" tone="fg">
        {`${joinNames(offRouteStores.map((store) => store.name))} ${offRouteStores.length === 1 ? 'není' : 'nejsou'} na trase – ${plural(offRouteCount, 'položka půjde', 'položky půjdou', 'položek půjde')} do obchodu ${firstStore.name}.`}
      </Paragraph>
      <Button compact disabled={routeBusy} onClick={() => void addToRoute()}>
        {offRouteStores.length === 1 ? `Přidat ${offRouteStores[0]?.name ?? ''} na trasu` : 'Přidat obchody na trasu'}
      </Button>
    </div>
  )

  async function confirm() {
    const changedAt = new Date().toISOString()
    const suggestTitle = template && !template.regular && !trip.title?.trim() && adds.length > 0 ? template.name : undefined
    const changes: SyncChangeData[] = []
    for (const { ci, item, qty, price } of plan) {
      const currency = currencyFor(ci)
      // Cena z akce se zapamatuje jako akce polozky (stitek „Z akce", Zrušit akci).
      const offer = prices.get(ci.id)?.offer
      if (qty === null) {
        if (item) changes.push({ op: 'remove', itemId: item.id, changedAt })
      } else if (!item) {
        changes.push({
          op: 'add',
          changedAt,
          item: {
            id: newId(),
            catalogItemId: ci.id,
            name: ci.name,
            storeId: storeIdFor(ci),
            qty,
            unit: ci.unit,
            expectedPriceMinor: price,
            expectedCurrency: currency,
            note: ci.note,
            source: 'template',
            deal:
              offer && offer.priceMinor !== null
                ? {
                    offerId: offer.id,
                    name: offer.name.slice(0, 500),
                    validTo: offer.validTo,
                    requiresCard: offer.requiresCard,
                    prev: { priceMinor: ci.typicalPriceMinor, currency, storeId: storeIdFor(ci) },
                  }
                : null,
          },
        })
      } else {
        const patch: TripItemPatch = {
          ...(item.qty !== qty ? { qty } : {}),
          ...(offer
            ? offerPatch(item, { storeId: item.storeId, priceMinor: offer.priceMinor, currency: offer.currency, name: offer.name, offer })
            : item.expectedPriceMinor !== price
              ? { expectedPriceMinor: price, expectedCurrency: currency, ...(item.deal ? { deal: null } : {}) }
              : {}),
        }
        if (Object.keys(patch).length > 0) changes.push({ op: 'patch', itemId: item.id, patch, changedAt })
      }
    }
    // Nakup jde pres frontu i bez signalu; koncept se hned vycisti, aby
    // opakovane potvrzeni nepridalo polozky dvakrat.
    sync.enqueue(changes)
    const toTemplate = templatePrices.map((p) => ({ ci: p.ci, price: p.price, currency: currencyFor(p.ci) }))
    setDraft(new Map())
    setPrices(new Map())
    if (toTemplate.length === 0) {
      back(suggestTitle)
      return
    }

    setSaving(true)
    setError(null)
    try {
      for (const { ci, price, currency } of toTemplate) {
        const updated = await api.catalog.update(ci.id, {
          typicalPriceMinor: price,
          typicalCurrency: price === null ? null : currency,
        })
        catalog.setData((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      }
      back(suggestTitle)
    } catch (err) {
      setError(
        err instanceof HttpError && err.isOffline
          ? 'Nákup je uložený. Cenu do šablony jde zapsat jen se signálem.'
          : `Nákup je uložený, ale šablona ne: ${errorMessage(err)}`,
      )
    } finally {
      setSaving(false)
    }
  }

  const now = Date.now()
  const needle = searchKey(query)
  const visible = items.filter((ci) => {
    if (needle && !searchKey(ci.name).includes(needle)) return false
    if (filter === 'due') return isDue(ci)
    if (filter === 'stale') return !ci.lastBoughtAt || now - Date.parse(ci.lastBoughtAt) > STALE_MS
    if (filter !== 'all') return ci.storeId === filter
    return true
  })
  const order =
    filter === 'stale'
      ? (a: CatalogItem, b: CatalogItem) => (a.lastBoughtAt ?? '').localeCompare(b.lastBoughtAt ?? '')
      : filter === 'due'
        ? (a: CatalogItem, b: CatalogItem) =>
            (rhythms.get(b.id)?.rhythm.dueRatio ?? 0) - (rhythms.get(a.id)?.rhythm.dueRatio ?? 0)
        : (a: CatalogItem, b: CatalogItem) => a.name.localeCompare(b.name, 'cs')
  const dueCount = items.filter(isDue).length
  const sections = [
    ...groupStores.map((store) => ({ key: store.id, title: store.name, items: visible.filter((ci) => ci.storeId === store.id) })),
    {
      key: 'none',
      title: 'Bez obchodu',
      items: visible.filter((ci) => !ci.storeId || !groupStores.some((store) => store.id === ci.storeId)),
    },
  ]
    .filter((section) => section.items.length > 0)
    .map((section) => ({ ...section, items: [...section.items].sort(order) }))

  const pricingItem = pricing ? (byId.get(pricing) ?? null) : null
  const offersByItem = matchesByItem(offerMatches.res.state === 'ok' ? offerMatches.res.data : [])

  const confirmLabel = saving
    ? 'Ukládám…'
    : !changed
      ? 'Beze změny'
      : onlyAdds
        ? `Přidat ${plural(adds.length, 'položku', 'položky', 'položek')}`
        : 'Uložit výběr'

  const chips = (
    <>
      <Chip active={filter === 'all'} onClick={() => setFilter('all')}>
        Vše
      </Chip>
      {dueCount > 0 && (
        <Chip active={filter === 'due'} onClick={() => setFilter('due')}>
          {`Asi dochází (${dueCount})`}
        </Chip>
      )}
      {/* Filtr jen obchody nakupu — cely seznam obchodu skupiny byl dlouha rada cipu. */}
      {(route.length > 1 ? route : route.length === 0 ? groupStores : []).map((store) => (
        <Chip key={store.id} active={filter === store.id} onClick={() => setFilter(store.id)}>
          {storeShortName(store.name)}
        </Chip>
      ))}
      <Chip active={filter === 'stale'} onClick={() => setFilter('stale')}>
        Dlouho nekoupeno
      </Chip>
    </>
  )

  const templateChips = templateList.length > 1 && (
    <>
      {templateList.map((t) => (
        <Chip key={t.id} active={t.id === template?.id} onClick={() => setTemplateId(t.id)}>
          {t.name}
        </Chip>
      ))}
    </>
  )
  // Vybrat vse a pak par veci odskrtnout je casto rychlejsi nez klikat po jedne (Richard 2026-10-13)
  // — i v Pravidelnem nakupu, kde se nic nepredvybira. Plati pro to, co je prave videt (filtr, hledani).
  const unpicked = visible.filter((ci) => qtyOf(ci.id) === null)
  const draftPicked = visible.filter((ci) => !existing.has(ci.id) && draft.has(ci.id) && draft.get(ci.id) !== null)
  const bulk = (unpicked.length > 1 || draftPicked.length > 0) && (
    <div className="button-row self-start">
      {unpicked.length > 1 && (
        <Button
          kind="text"
          compact
          onClick={() =>
            setDraft((prev) => {
              const next = new Map(prev)
              for (const ci of unpicked) next.set(ci.id, defaultQtyOf(ci))
              return next
            })
          }
        >
          {`Vybrat všech ${unpicked.length}`}
        </Button>
      )}
      {draftPicked.length > 0 && (
        <Button
          kind="text"
          compact
          onClick={() =>
            setDraft((prev) => {
              const next = new Map(prev)
              for (const ci of draftPicked) next.delete(ci.id)
              return next
            })
          }
        >
          Odznačit vše
        </Button>
      )}
    </div>
  )

  const states = (
    <>
      {error && <Paragraph tone="error">{error}</Paragraph>}
      {offRouteNotice}
      {catalog.res.state === 'loading' && <Spinner />}
      {catalog.res.state === 'error' && <Paragraph tone="error">{catalog.res.message}</Paragraph>}
      {catalog.res.state === 'ok' && items.length === 0 && (
        <EmptyState title="Šablona je prázdná" action={<Button onClick={() => navigate(template ? `/sablony/${template.id}` : '/sablony')}>Otevřít šablonu</Button>}>
          Věci, které kupujete pravidelně, si přidej do šablony. Tady je pak jen odškrtneš.
        </EmptyState>
      )}
      {items.length > 0 && sections.length === 0 && <Paragraph>Nic takového v šabloně není.</Paragraph>}
    </>
  )

  function sectionMeta(sectionKey: string): string | undefined {
    return sectionKey !== 'none' && !route.some((s) => s.id === sectionKey) ? 'není na trase' : undefined
  }

  function renderPickRow(ci: CatalogItem) {
    const qty = qtyOf(ci.id)
    const selected = qty !== null
    const currency = currencyFor(ci)
    const price = priceOf(ci)
    const known = rhythms.get(ci.id)
    const bought = known
      ? `${everyText(known.rhythm.intervalDays)} · naposledy ${relativeAgo(ci.lastBoughtAt ?? known.last)}${isDue(ci) ? ' · asi dochází' : ''}`
      : ci.lastBoughtAt
        ? relativeAgo(ci.lastBoughtAt)
        : 'zatím nekoupeno'
    const store = stores.find((s) => s.id === storeIdFor(ci)) ?? null
    const offer = offersForStore(offersByItem.get(ci.id), store).find((o) => !currency || o.currency === currency) ?? null
    // U vybrane polozky je cena na vlastnim radku, tady by byla dvakrat.
    const sub = selected
      ? bought
      : [bought, ci.typicalPriceMinor !== null && currency ? formatMoney(ci.typicalPriceMinor, currency) : null]
          .filter(Boolean)
          .join(' · ')
    return (
      <SelectRow key={ci.id} selected={selected} className="pick-row">
        <div className="pick-row-top">
          <button
            type="button"
            className="row-main"
            aria-pressed={selected}
            onClick={() => select(ci, selected ? null : defaultQtyOf(ci))}
          >
            <Checkbox checked={selected} onRow />
            <TextCol title={ci.name} sub={sub} subTone={selected ? 'fg' : 'muted'} />
          </button>
          {qty !== null && (
            <Stepper
              value={formatQty(qty, ci.unit)}
              decrementLabel={`Méně: ${ci.name}`}
              incrementLabel={`Více: ${ci.name}`}
              onDecrement={() => select(ci, qty - 1)}
              onIncrement={() => select(ci, qty + 1)}
            />
          )}
        </div>
        {selected && (
          <button type="button" className="pick-price" onClick={() => setPricing(ci.id)}>
            <Icon name="tag" size={16} />
            <span>{price !== null && currency ? `${formatMoney(price, currency)} / ${ci.unit ?? 'ks'}` : 'Bez ceny'}</span>
            <span className="pick-price-edit">{price !== null ? 'Změnit' : 'Přidat cenu'}</span>
          </button>
        )}
        {offer && (
          <div className="pick-offer">
            <OfferBadge offer={offer} onClick={() => setOfferOpen({ offer, ci })} />
          </div>
        )}
      </SelectRow>
    )
  }

  const priceSheet = (
    <Sheet
      open={pricingItem !== null}
      onClose={() => setPricing(null)}
      title={pricingItem?.name ?? ''}
      subtitle={pricingItem ? `Cena za ${pricingItem.unit ?? 'ks'}` : undefined}
    >
      {pricingItem && (
        <PriceForm
          key={pricingItem.id}
          item={pricingItem}
          currency={currencyFor(pricingItem)}
          current={priceOf(pricingItem)}
          toTemplate={prices.get(pricingItem.id)?.toTemplate ?? true}
          onSave={(value) => {
            setPrices((prev) => new Map(prev).set(pricingItem.id, value))
            setPricing(null)
          }}
        />
      )}
    </Sheet>
  )

  const offerSheet = (
    <OfferSheet
      offer={offerOpen?.offer ?? null}
      catalogItem={offerOpen?.ci ?? null}
      onClose={() => setOfferOpen(null)}
      onUsePrice={
        offerOpen && offerOpen.offer.currency === (currencyFor(offerOpen.ci) ?? offerOpen.offer.currency)
          ? () => {
              const { offer, ci } = offerOpen
              if (qtyOf(ci.id) === null) select(ci, defaultQtyOf(ci))
              setPrices((prev) => new Map(prev).set(ci.id, { minor: offer.priceMinor, toTemplate: false, offer }))
              setOfferOpen(null)
            }
          : undefined
      }
    />
  )

  if (desktop) {
    // Panel Vybráno: vse, co pujde na nakup, bez ohledu na hledani a filtr.
    const chosen = items.filter((ci) => qtyOf(ci.id) !== null).sort((a, b) => a.name.localeCompare(b.name, 'cs'))
    const chosenGroups = [
      ...groupStores.map((store) => ({ key: store.id, title: store.name, items: chosen.filter((ci) => storeIdFor(ci) === store.id) })),
      {
        key: 'none',
        title: 'Bez obchodu',
        items: chosen.filter((ci) => !groupStores.some((store) => store.id === storeIdFor(ci))),
      },
    ].filter((group) => group.items.length > 0)

    const rates = ratesFor(trip, fx)
    const byCurrency: Estimate['byCurrency'] = {}
    let czkMinor = 0
    let convertible = true
    let withoutPrice = 0
    for (const ci of chosen) {
      const price = priceOf(ci)
      const currency = currencyFor(ci)
      if (price === null || currency === null) {
        withoutPrice += 1
        continue
      }
      const line = Math.round(price * (qtyOf(ci.id) ?? 0))
      byCurrency[currency] = (byCurrency[currency] ?? 0) + line
      const czk = toCzkMinor(line, currency, rates)
      if (czk === null) convertible = false
      else czkMinor += czk
    }
    const any = Object.keys(byCurrency).length > 0
    const estimate: Estimate = { byCurrency, czkMinor: any && convertible ? czkMinor : null, withoutPrice }
    const estimateLine = estimateText(estimate, route.map((store) => store.currency))

    return (
      <div className="desk-view">
        <div className="desk-view-main">
          <DeskToolbar
            crumb={[tripName(trip, stores).title, tripName(trip, stores).date].filter(Boolean).join(' · ')}
            title="Vybrat ze šablony"
            actions={
              <TextField
                type="search"
                aria-label="Hledat v šabloně"
                placeholder="Hledat v šabloně"
                className="desk-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            }
          />
          {templateChips && (
            <div className="chips" role="group" aria-label="Šablona">
              {templateChips}
            </div>
          )}
          {items.length > 0 && <div className="chips">{chips}</div>}
          {bulk}
          {states}
          {sections.length > 0 && (
            <div className="board">
              {sections.map((section) => {
                const picked = section.items.filter((ci) => qtyOf(ci.id) !== null).length
                return (
                  <Panel
                    key={section.key}
                    className="board-col"
                    title={section.title}
                    meta={sectionMeta(section.key) ?? `${picked} z ${section.items.length} vybráno`}
                  >
                    <div className="board-col-rows">{section.items.map(renderPickRow)}</div>
                  </Panel>
                )
              })}
            </div>
          )}
        </div>

        <Inspector label="Vybráno">
          <div className="inspector-head">
            <h2 className="inspector-title">Vybráno</h2>
            <span className="muted">{itemsCount(chosen.length)}</span>
          </div>
          {chosenGroups.length === 0 && <Paragraph size="meta">Zatím nic. Označ, co chceš tentokrát koupit.</Paragraph>}
          {chosenGroups.map((group) => (
            <div key={group.key} className="stack-4">
              <Label as="span">{group.title}</Label>
              {group.items.map((ci) => (
                <MoneyLine key={ci.id} label={ci.name} value={formatQty(qtyOf(ci.id) ?? 0, ci.unit)} strong={false} />
              ))}
            </div>
          ))}
          {estimateLine && (
            <div className="stack-4">
              <Label as="span">Odhad</Label>
              <span className="tabular">{estimateLine}</span>
              {withoutPrice > 0 && <Paragraph size="meta">{`${itemsCount(withoutPrice)} bez ceny`}</Paragraph>}
            </div>
          )}
          <div className="button-row">
            <Button onClick={() => back()}>{changed ? 'Zrušit' : 'Zavřít'}</Button>
            <Button kind="primary" className="grow" disabled={!changed || saving} onClick={() => void confirm()}>
              {confirmLabel}
            </Button>
          </div>
        </Inspector>
        {priceSheet}
        {offerSheet}
      </div>
    )
  }

  return (
    <div className="app-shell">
      <div className="page">
        <ScreenHeader
          title="Vybrat ze šablony"
          leftIcon="x"
          backLabel="Zavřít"
          onBack={() => back()}
          right={
            <IconButton
              icon="search"
              label="Hledat v šabloně"
              aria-pressed={searching}
              onClick={() => {
                setSearching((v) => !v)
                setQuery('')
              }}
            />
          }
        />
        <main className="page-content stack-8">
          <Paragraph>Označ, co chceš koupit tentokrát. U každé věci vidíš, kdy se naposledy kupovala.</Paragraph>
          {searching && (
            <TextField
              type="search"
              aria-label="Hledat v šabloně"
              placeholder="Hledat v šabloně"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          {templateChips && (
            <ScrollRow className="chips-scroll" role="group" ariaLabel="Šablona">
              {templateChips}
            </ScrollRow>
          )}
          {items.length > 0 && <ScrollRow className="chips-scroll">{chips}</ScrollRow>}
          {bulk}
          {states}

          {sections.map((section) => (
            <section key={section.key} className="stack-8">
              <SectionHead title={section.title} meta={sectionMeta(section.key)} />
              {section.items.map(renderPickRow)}
            </section>
          ))}
        </main>
        <Footer>
          <div className="button-row">
            <Button onClick={() => back()}>{changed ? 'Zrušit' : 'Zavřít'}</Button>
            <Button kind="primary" className="grow" disabled={!changed || saving} onClick={() => void confirm()}>
              {confirmLabel}
            </Button>
          </div>
        </Footer>
      </div>
      {priceSheet}
      {offerSheet}
    </div>
  )
}

function PriceForm({
  item,
  currency,
  current,
  toTemplate,
  onSave,
}: {
  item: CatalogItem
  currency: Currency | null
  current: number | null
  toTemplate: boolean
  onSave(value: PriceDraft): void
}) {
  const [text, setText] = useState(priceInputValue(current))
  const [saveToTemplate, setSaveToTemplate] = useState(toTemplate)
  const [error, setError] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    const minor = parsePriceInput(text)
    if (minor === 'invalid') {
      setError('Cena musí mít tvar 4,99')
      return
    }
    onSave({ minor, toTemplate: saveToTemplate })
  }

  return (
    <form className="stack" onSubmit={submit}>
      <TextField
        label={`Cena${currency ? ` (${currencySymbol(currency)})` : ''}`}
        hint={
          item.typicalPriceMinor !== null && item.typicalCurrency
            ? `V šabloně ${formatMoney(item.typicalPriceMinor, item.typicalCurrency)}. Prázdné pole = bez ceny.`
            : 'V šabloně zatím cena není. Prázdné pole = bez ceny.'
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
        inputMode="decimal"
        placeholder="4,99"
        error={error}
        autoFocus
        // Pole se otevira predvyplnene; cena se obvykle prepisuje cela.
        onFocus={(e) => e.currentTarget.select()}
      />
      <button
        type="button"
        className="store-pick"
        aria-pressed={saveToTemplate}
        onClick={() => setSaveToTemplate((v) => !v)}
      >
        <Checkbox checked={saveToTemplate} />
        <TextCol title="Uložit i do šablony" sub="Příště se nabídne tahle cena" />
      </button>
      <Button kind="primary" type="submit" full>
        Použít cenu
      </Button>
    </form>
  )
}
